import { useState, useCallback, useEffect, type ReactNode } from 'react'
import { LayerDialog, Input, Banner } from '@cloudflare/kumo'
import { CheckIcon, CopyIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { useTranslation } from '../../../i18n'

export type DeleteResourceSize = 'sm' | 'base'

export interface DeleteResourceProps {
  /** Whether the dialog is open */
  open: boolean
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void
  /** The type of resource being deleted (e.g., "building", "room", "network") */
  resourceType: string
  /** The name of the specific resource being deleted */
  resourceName: string
  /** Callback when delete is confirmed */
  onDelete: () => void | Promise<void>
  /** Whether the delete action is in progress */
  isDeleting?: boolean
  /** Whether the confirmation input should be case-sensitive (default: true) */
  caseSensitive?: boolean
  /** Custom delete button text (defaults to t('deleteResource.deleteButton')) */
  deleteButtonText?: string
  /** Dialog size */
  size?: DeleteResourceSize
  /** Error message to display if the delete action fails */
  errorMessage?: string
  /** Optional extra note rendered under the warning paragraph (e.g. cascade warnings) */
  warningNote?: ReactNode
}

export function DeleteResource({
  open,
  onOpenChange,
  resourceType,
  resourceName,
  onDelete,
  isDeleting = false,
  caseSensitive = true,
  deleteButtonText,
  size = 'base',
  errorMessage,
  warningNote,
}: DeleteResourceProps) {
  const { t } = useTranslation()
  const [confirmationInput, setConfirmationInput] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!open) {
      setConfirmationInput('')
      setCopied(false)
    }
  }, [open])

  const normalizeForComparison = useCallback(
    (str: string) => (caseSensitive ? str : str.toLowerCase()),
    [caseSensitive],
  )

  const isConfirmed =
    normalizeForComparison(confirmationInput) === normalizeForComparison(resourceName)

  const handleDelete = useCallback(async () => {
    if (!isConfirmed || isDeleting) return
    await onDelete()
  }, [isConfirmed, isDeleting, onDelete])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(resourceName)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard access can fail (permissions denied, insecure context) — non-fatal.
    }
  }, [resourceName])

  return (
    <LayerDialog.Alert
      open={open}
      onOpenChange={(next) => onOpenChange(next)}
      dismissDisabled={isDeleting}
    >
      <LayerDialog.Content size={size}>
        <LayerDialog.Title>{t('deleteResource.title', { resourceName })}</LayerDialog.Title>
        <LayerDialog.Body>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              {errorMessage && (
                <Banner icon={<WarningCircleIcon />} variant="error">
                  {errorMessage}
                </Banner>
              )}
              <p className="text-sm text-kumo-subtle max-w-prose text-pretty">
                {t('deleteResource.warning', { resourceType, resourceName })}
              </p>
              {warningNote && (
                <p className="text-sm text-kumo-subtle max-w-prose text-pretty">{warningNote}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1 text-sm">
                <span>
                  {t('deleteResource.typePrefix')}{' '}
                  <button
                    type="button"
                    className="font-mono text-sm inline font-semibold bg-kumo-tint hover:bg-kumo-fill rounded-md px-2 py-1 group hover:cursor-pointer"
                    onClick={() => void handleCopy()}
                    aria-label={t('deleteResource.copyAria', { resourceName })}
                  >
                    {resourceName}
                    {copied ? (
                      <CheckIcon size={12} weight="bold" className="inline ml-1" />
                    ) : (
                      <CopyIcon
                        size={12}
                        weight="bold"
                        className="inline text-kumo-subtle group-hover:text-kumo-default ml-1"
                      />
                    )}
                  </button>{' '}
                  {t('deleteResource.typeSuffix')}
                </span>
              </div>
              <Input
                placeholder={resourceName}
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                disabled={isDeleting}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                aria-label={`${t('deleteResource.typePrefix')} ${resourceName} ${t('deleteResource.typeSuffix')}`}
                className="w-full"
              />
            </div>
          </div>
        </LayerDialog.Body>
        <LayerDialog.Actions dismissLabel={t('common.cancel')}>
          <LayerDialog.Action
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={!isConfirmed || isDeleting}
            loading={isDeleting}
          >
            {deleteButtonText || t('deleteResource.deleteButton', { resourceType })}
          </LayerDialog.Action>
        </LayerDialog.Actions>
      </LayerDialog.Content>
    </LayerDialog.Alert>
  )
}

DeleteResource.displayName = 'DeleteResource'
